import { memo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { derivePartsFromLegacy } from '../stores/chatStore';
import { lookupToolRenderer } from '../tools';
import type { ChatMessage as ChatMessageType } from '../types';
import { ToolCallDisplay } from '../ToolCallDisplay';
import styles from './ChatMessage.module.css';

interface ChatMessageProps {
  message: ChatMessageType;
  isStreaming?: boolean;
  onConfirmToolCall?: (toolCallId: string) => void;
  onCancelToolCall?: (toolCallId: string) => void;
  onAnswerToolCall?: (toolCallId: string, answerText: string) => void;
}

function normalizeGaussContent(content: string): string {
  return content
    .replace(/I['’]?m the Bastion Assistant/gi, "I'm Gauss")
    .replace(/I am the Bastion Assistant/gi, "I am Gauss")
    .replace(/Bastion Assistant/gi, "Gauss");
}

/**
 * A table in an answer is in its own box. On a phone and on touch, a table
 * that is wider than the answer scrolls in the box, and the message list
 * does not scroll sideways (design system README, "Phone and touch").
 */
const MARKDOWN_COMPONENTS: Components = {
  table: ({ node: _node, ...props }) => (
    <div className={styles.tableScroll}>
      <table {...props} />
    </div>
  ),
};

/**
 * Renders a markdown text part. Memoized so finished text parts don't
 * re-parse markdown when later parts in the same message stream in. The
 * trailing "live" text part is opted out of memo via a `live` prop so it
 * still gets fresh content on each store update.
 */
const TextPartView = memo(
  function TextPartView({
    content,
    isUser,
  }: {
    content: string;
    isUser: boolean;
  }) {
    if (isUser) {
      return <div className={styles.userText}>{content}</div>;
    }
    const display = normalizeGaussContent(content);
    return (
      <div className={styles.markdown}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
          {display}
        </ReactMarkdown>
      </div>
    );
  },
);

export function ChatMessage({
  message,
  isStreaming = false,
  onConfirmToolCall,
  onCancelToolCall,
  onAnswerToolCall,
}: ChatMessageProps) {
  const isUser = message.role === 'user';
  const parts = message.parts ?? derivePartsFromLegacy(message);
  const hasContent = parts.length > 0;

  return (
    <div
      className={`${styles.message} ${isUser ? styles.userMessage : styles.assistantMessage}`}
    >
      {isUser ? (
        <div className={styles.userBubble}>
          {parts.map((part, idx) =>
            part.type === 'text' ? (
              <TextPartView key={idx} content={part.content} isUser />
            ) : null,
          )}
        </div>
      ) : (
        <div className={styles.assistantBlocks}>
          {parts.map((part, idx) => {
            if (part.type === 'tool') {
              const CustomRenderer = lookupToolRenderer(part.toolCall.name);
              if (CustomRenderer) {
                return (
                  <CustomRenderer
                    key={part.toolCall.id ?? idx}
                    toolCall={part.toolCall}
                    onConfirm={onConfirmToolCall}
                    onCancel={onCancelToolCall}
                    onAnswer={onAnswerToolCall}
                  />
                );
              }
              return (
                <ToolCallDisplay
                  key={part.toolCall.id ?? idx}
                  toolCall={part.toolCall}
                  onConfirm={onConfirmToolCall}
                  onCancel={onCancelToolCall}
                />
              );
            }
            return (
              <TextPartView
                key={idx}
                content={part.content}
                isUser={false}
              />
            );
          })}

          {/* Subtle pulse only while we have no content yet (model is "thinking"). */}
          {isStreaming && !hasContent && (
            <span className={styles.thinkingDot} aria-label="Thinking" />
          )}
        </div>
      )}
    </div>
  );
}
